import Pagination from '@/layout/Pagination'
import type {GrubhubOrder} from '@/lib/food-grubhub/foodGrubhubModel'
import useGrubhubGetFoodOrdersQuery from '@/lib/food-grubhub/hooks/useGrubhubGetFoodOrders'
import {useLocale} from '@/lib/locale/locale'
import {FoodOrdersRoute} from '@/routeRegistry'
import useSearch from '@/utils/router/useSearch'

import FoodOrderItem from './FoodOrderItem'
import style from './FoodOrders.module.scss'

const ITEMS_PER_PAGE = 10

export default memo(function FoodOrders() {
  const [locale] = useLocale()
  const router = useRouter()

  const {data: orders, isLoading} = useGrubhubGetFoodOrdersQuery(locale)
  const totalOrders = orders?.records.length ?? 0

  const [page, setPage] = useSearch(FoodOrdersRoute.id, 'page', {
    replace: false,
    resetScroll: true,
    hashScrollIntoView: {behavior: 'smooth', block: 'start', inline: 'nearest'},
  })

  const itemsPerPage = ITEMS_PER_PAGE
  const pageCount = totalOrders > 0 ? Math.ceil(totalOrders / itemsPerPage) : 1
  const startIndex = (page - 1) * itemsPerPage
  const currentPageItems = orders?.records.slice(startIndex, startIndex + itemsPerPage) ?? []

  useEffect(
    function correctPage() {
      if (!orders?.records.length) return
      if (pageCount > 0 && page > pageCount) {
        void setPage(pageCount)
      }
    },
    [page, pageCount, orders],
  )

  const handlePageChange = useCallback((event: {selected: number}) => {
    void setPage(event.selected + 1)
  }, [])

  ;(function correctPage() {
    if (!orders?.records.length) return
    if (page > pageCount) void setPage(pageCount)
  })()

  const hrefBuilder = useCallback(
    (pageNum: number) =>
      router.buildLocation({to: FoodOrdersRoute.fullPath, search: {page: pageNum}}).href,
    [router],
  )

  return (
    <main className={style.OrdersSection} aria-live='polite'>
      <h1 className={style.Heading} aria-label={`${totalOrders} food orders`}>
        Food Orders ({totalOrders})
      </h1>

      {isLoading && <div>Loading food orders...</div>}
      {!isLoading && totalOrders === 0 && <div aria-label='No food orders'>No food orders.</div>}
      {totalOrders > 0 && (
        <div className={style.Orders}>
          {currentPageItems.map((order: GrubhubOrder) => (
            <FoodOrderItem key={order.id} order={order} />
          ))}
        </div>
      )}
      {pageCount > 1 && (
        <Pagination
          onPageChange={handlePageChange}
          pageCount={pageCount}
          currentPage={page - 1}
          hrefBuilder={hrefBuilder}
        />
      )}
    </main>
  )
})
